import { memo } from 'react';

import type { AdditionalStep } from './Timeline.types';

import './AdditionalSteps.css';

/**
 * The aggregate-window steps of a timeline, rendered as labelled boxes to the right of the track.
 *
 * Deliberately not cells on the slider: a window covering a day or three days next to six 1-hourly
 * cells of the same width would read as a uniform cadence. They do, however, share the slider's
 * index space - `next` from the last slider step lands on the first button here - so the parent owns
 * the selection and this component is presentational.
 *
 * The group can legitimately have nothing selected (the slider owns the selection then), which is
 * why these are pressed buttons rather than a radiogroup.
 */
export default memo(function AdditionalSteps(props: {
    steps: AdditionalStep[],
    /** Index into `steps`, or null when the selection lives on the slider. */
    activeIndex: number | null,
    disabled?: boolean,
    onSelect: (index: number) => void,
}) {
    // Nothing rather than an empty flex row, so a layer without windows gets no stray gap.
    if (!props.steps.length) return null;

    return <div className='efas-additional-steps'>
        {props.steps.map((step, index) => {
            const isActive = index === props.activeIndex;
            // The button IS the box, and the caption hangs off it absolutely - exactly as
            // .efas-timeline-icon does over the track. A caption in flow would make the box the
            // upper half of a two-row control and push it off the track's centreline.
            return <button key={`${index}-${step.sliderLabel}`}
                type='button'
                className={`efas-additional-step ${isActive ? 'active' : ''}`}
                aria-pressed={isActive}
                aria-label={`Forecast summary ${step.sliderLabel}`}
                disabled={props.disabled}
                onClick={() => props.onSelect(index)}>
                <span className='efas-additional-step-label'>{step.sliderLabel}</span>
            </button>;
        })}
    </div>;
})
